/**
 * LoginDialog — the BisaWofa sign-in modal, in the standard frosted-glass
 * look (see BisaWofa-Form-Design-Principle.md).
 *
 * Replaces the Bootstrap modal that shipped with the authentication repo.
 * Two steps in one card:
 *   1. email  → requestOtp()
 *   2. code   → verifyOtp()
 *
 * Modes:
 *   dismissible: false  (hard gate) — no backdrop-close, no Escape, no cancel
 *                                     button. Used while the app is in
 *                                     development so passers-by are blocked.
 *   dismissible: true   (soft gate) — user may close and browse anonymously.
 *
 * @module components/LoginDialog
 */

import { injectGlassStyles } from './glassForm.js';
import { requestOtp, verifyOtp } from '../auth.js';

/**
 * Show the login dialog.
 * @param {object} [opts]
 * @param {boolean} [opts.dismissible=false]  allow closing without signing in
 * @param {string}  [opts.subtitle]           line under the title
 * @returns {Promise<{ ok:boolean, email:string|null }>} resolves when signed in
 *          (ok:true) or when dismissed (ok:false)
 */
export function openLoginDialog(opts = {}) {
  injectGlassStyles();
  _ensureLoginStyles();
  const dismissible = opts.dismissible === true;
  const subtitle = opts.subtitle
    || (dismissible ? 'Sign in to add and manage places'
                    : 'BisaWofa is in private testing — please sign in');

  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'glass-overlay';
    overlay.innerHTML = `
      <div class="glass-card login-card" role="dialog" aria-modal="true" aria-labelledby="login-title">
        <div class="glass-grabber" aria-hidden="true"></div>
        <header class="glass-header">
          <div class="glass-icon" aria-hidden="true"><i class="bi bi-person-lock"></i></div>
          <div>
            <h2 id="login-title" class="glass-title">Sign in</h2>
            <p class="glass-subtitle">${_esc(subtitle)}</p>
          </div>
        </header>

        <div class="glass-body">
          <label class="glass-field" data-step="email">
            <span class="glass-label">Email</span>
            <input type="email" class="glass-input" id="login-email"
                   inputmode="email" autocomplete="email"
                   placeholder="you@example.com" maxlength="180">
          </label>

          <label class="glass-field login-otp-field" data-step="otp" hidden>
            <span class="glass-label">Verification code</span>
            <input type="text" class="glass-input login-otp-input" id="login-otp"
                   inputmode="numeric" autocomplete="one-time-code"
                   placeholder="6-digit code" maxlength="6">
          </label>

          <div class="login-msg" hidden></div>
        </div>

        <footer class="glass-footer">
          ${dismissible ? '<button type="button" class="glass-btn glass-btn-ghost" data-dismiss>Not now</button>' : ''}
          <button type="button" class="glass-btn glass-btn-ghost login-back" hidden data-back>Back</button>
          <button type="button" class="glass-btn glass-btn-primary" data-primary>Send code</button>
        </footer>
      </div>`;

    document.body.appendChild(overlay);
    requestAnimationFrame(() => overlay.classList.add('glass-open'));

    const emailField = overlay.querySelector('[data-step="email"]');
    const otpField   = overlay.querySelector('[data-step="otp"]');
    const emailInput = overlay.querySelector('#login-email');
    const otpInput   = overlay.querySelector('#login-otp');
    const msgEl      = overlay.querySelector('.login-msg');
    const primary    = overlay.querySelector('[data-primary]');
    const backBtn    = overlay.querySelector('[data-back]');

    setTimeout(() => emailInput.focus(), 140);

    let step = 'email';
    let busy = false;
    let settled = false;

    const say = (text, kind = 'error') => {
      msgEl.textContent = text;
      msgEl.className = `login-msg login-msg-${kind}`;
      msgEl.hidden = !text;
    };
    const setBusy = (b, label) => {
      busy = b;
      primary.disabled = b;
      primary.textContent = label || (step === 'email' ? 'Send code' : 'Sign in');
    };
    const finish = (result) => {
      if (settled) return;
      settled = true;
      overlay.classList.remove('glass-open');
      overlay.addEventListener('transitionend', () => overlay.remove(), { once: true });
      setTimeout(() => overlay.remove(), 350);
      document.removeEventListener('keydown', onKey);
      resolve(result);
    };

    const goToOtp = () => {
      step = 'otp';
      emailField.hidden = true;
      otpField.hidden = false;
      backBtn.hidden = false;
      primary.textContent = 'Sign in';
      setTimeout(() => otpInput.focus(), 60);
    };
    const goToEmail = () => {
      step = 'email';
      otpField.hidden = true;
      emailField.hidden = false;
      backBtn.hidden = true;
      primary.textContent = 'Send code';
      say('');
      setTimeout(() => emailInput.focus(), 60);
    };

    const submit = async () => {
      if (busy) return;
      say('');
      if (step === 'email') {
        const email = emailInput.value.trim();
        if (!email) { say('Please enter your email address.'); emailInput.focus(); return; }
        setBusy(true, 'Sending…');
        const r = await requestOtp(email);
        setBusy(false);
        if (!r.ok) { say(r.msg); return; }
        say(r.msg, 'success');
        goToOtp();
      } else {
        const code = otpInput.value.trim();
        if (!code) { say('Please enter the code from your email.'); otpInput.focus(); return; }
        setBusy(true, 'Signing in…');
        const r = await verifyOtp(code);
        setBusy(false);
        if (!r.ok) { say(r.msg); otpInput.select(); return; }
        finish({ ok: true, email: r.email });
      }
    };

    overlay.addEventListener('click', (e) => {
      if (e.target.closest('[data-primary]')) { submit(); return; }
      if (e.target.closest('[data-back]')) { goToEmail(); return; }
      if (e.target.closest('[data-dismiss]')) { finish({ ok: false, email: null }); return; }
      // Backdrop click only closes in soft-gate mode.
      if (e.target === overlay && dismissible) finish({ ok: false, email: null });
    });

    overlay.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); submit(); }
    });

    const onKey = (e) => {
      // Escape only closes in soft-gate mode.
      if (e.key === 'Escape' && dismissible) { e.preventDefault(); finish({ ok: false, email: null }); }
    };
    document.addEventListener('keydown', onKey);
  });
}

// ---------- styles ----------

let _styled = false;
function _ensureLoginStyles() {
  if (_styled) return;
  _styled = true;
  const css = `
    .login-card { max-width: 400px; }
    .login-otp-input {
      letter-spacing: 0.35em; font-size: 1.2rem; font-weight: 700; text-align: center;
    }
    .login-msg { font-size: 0.86rem; line-height: 1.4; padding: 2px 2px 0; }
    .login-msg-error { color: #c0392b; }
    .login-msg-success { color: #2e7d32; }
    @media (prefers-color-scheme: dark) {
      .login-msg-error { color: #ff8a80; }
      .login-msg-success { color: #a5d6a7; }
    }
  `;
  const el = document.createElement('style');
  el.id = 'login-dialog-styles';
  el.textContent = css;
  document.head.appendChild(el);
}

function _esc(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}

export default { openLoginDialog };
